import { useState } from "react";
import { GUIDES } from "../lib/guides";
import { readPref, writePref } from "../lib/prefs";

/**
 * The collapsible "How this step works" panel on every screen, so participants can work with minimal supervision.
 * Open by default until the step is done; the participant's open/closed choice is remembered per guide.
 */
export function Guide({ guideKey, done }: { guideKey: string; done: boolean }) {
  const guide = GUIDES[guideKey];
  const stored = readPref(`guide.${guideKey}`);
  const [open, setOpen] = useState(stored ? stored === "open" : !done);
  if (!guide) return null;

  return (
    <details
      open={open}
      onToggle={(event) => {
        const next = (event.currentTarget as HTMLDetailsElement).open;
        setOpen(next);
        writePref(`guide.${guideKey}`, next ? "open" : "closed");
      }}
      className="mb-5 rounded-[var(--radius-card)] border border-border bg-surface-high px-4 py-3 print:hidden"
    >
      <summary className="flex cursor-pointer items-center justify-between gap-3 text-[14px]">
        <span className="font-bold text-accent">How this step works</span>
        <span className="text-[12px] text-text-muted">
          {guide.time} · {guide.steps.length} steps
        </span>
      </summary>
      <div className="mt-3 text-[13.5px]">
        {guide.need ? (
          <div className="mb-2">
            <b>Before you start, have ready:</b>
            <ul className="m-0 mt-1 pl-5">
              {guide.need.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <ol className="m-0 pl-5">
          {guide.steps.map((step) => (
            <li key={step} className="mb-1">
              {step}
            </li>
          ))}
        </ol>
        {guide.done ? (
          <p className="mb-0 mt-2">
            <b className="text-positive">✓ You're done when:</b> {guide.done}
          </p>
        ) : null}
        {guide.tip ? (
          <p className="mb-0 mt-2">
            <b>Tip:</b> {guide.tip}
          </p>
        ) : null}
        <p className="mb-0 mt-2 text-text-muted">
          Stuck? Your progress is saved, so you can stop and come back any time. For help, ask your
          cohort facilitator and mention the step number.
        </p>
      </div>
    </details>
  );
}
